import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2 } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { Block } from '@core/models/block/block.model';
import { ViewPart } from '@core/models/block-settings.model';
import { ApiInventoryService } from '@core/services/api/api-inventory.service';
import {
  ApiEditionConfigurationService,
  EditionChoice,
  EditionOptionGroup,
  LocaleMap,
  PriceConfiguration,
  PricedSelection,
} from '@core/services/api/api-edition-configuration.service';
import { PageService } from '@core/services/page/page.service';
import { cartSourceContextFromUrl } from '@shared/services/cart/cart-source-context';
import { ShoppingCartService } from '@shared/services/cart/shopping-cart.service';
import { EditionDimensionsService, formatSelectionDimensions } from '@shared/services/edition/edition-dimensions.service';
import { SessionStorageService } from '@shared/services/storage';

/** Język, na który cofamy się, gdy tłumaczenie dla języka strony nie istnieje. */
const DOMYSLNY_JEZYK = 'pl';

/** Klucze komunikatów pokazywanych, gdy odczyt z serwera się nie powiódł. */
const KOMUNIKAT_BLEDU_KONFIGURACJI = 'priceConfigurator.configurationError';
const KOMUNIKAT_BLEDU_WYCENY = 'priceConfigurator.priceError';

/** Nazwa kontrolki formularza dla parametru głównego (wyboru edycji). */
const KONTROLKA_EDYCJI = 'edition';

/** Przedrostek nazw kontrolek parametrów dodatkowych — po nim odtwarzamy identyfikator grupy. */
const PRZEDROSTEK_KONTROLKI_GRUPY = 'optionGroup_';

/**
 * Jedna lista rozwijana na ekranie: parametr główny (edycja) albo parametr dodatkowy.
 *
 * Liczba list zależy od pracy, a struktura bloku jest stała, więc nie trzyma ich wszystkich —
 * trzyma jeden wzorzec, z którego powstają te wpisy. Ten sam zabieg co z `templateLevel*`
 * w filtrach zaawansowanych.
 */
interface ParametrWidoku {
  /** Nazwa kontrolki w `formGroup`. */
  klucz: string;
  /**
   * Sam węzeł wzorca — wiersz parametru, czyli to, co w drzewie kreatora nosi nazwę „Edycja"
   * albo nazwę parametru dodatkowego.
   *
   * Trzymamy go obok dzieci, bo z jego `uId` bierze się stylowanie CAŁEGO wiersza: szerokość,
   * tło, obramowanie, odstępy. Wcześniej wzorzec służył wyłącznie za źródło dzieci i nigdy nie
   * docierał do szablonu, więc style zapisane na nim przez artystę generowały regułę, która
   * nie miała na czym usiąść.
   */
  wiersz: Block | null;
  /** Sklonowany węzeł etykiety z wstrzykniętą nazwą parametru. */
  etykieta: Block | null;
  /**
   * Sklonowany węzeł triggera (zamknięty wybór) z tekstem aktualnie wybranej opcji.
   * `null`, gdy starszy blok nie ma węzła triggera — wtedy szablon pokazuje prosty,
   * funkcjonalny trigger zastępczy.
   */
  trigger: Block | null;
  /** Węzeł wrappera wiersza opcji; z jego `uId` bierze się tło/obramowanie całego wiersza. */
  optionRow: Block | null;
  /** Węzeł panelu listy (kontener rozwiniętych opcji); z jego `uId` bierze się tło/obramowanie panelu. */
  panel: Block | null;
  /** Czy panel z opcjami jest rozwinięty. */
  otwarty: boolean;
  opcje: OpcjaWidoku[];
}

interface OpcjaWidoku {
  id: number;
  /** Sklonowany węzeł pozycji z wstrzykniętą nazwą opcji. */
  wezel: Block | null;
  wyprzedana: boolean;
}

/**
 * Konfigurator ceny na karcie pracy: parametr główny (edycja), parametry dodatkowe,
 * kwota i przycisk zakupu.
 *
 * Ten sam komponent obsługuje pracę w obu trybach. Praca bez edycji nie dostaje list
 * rozwijanych — zostaje kwota i przycisk. Dzięki temu artysta wstawia na stronę jeden
 * blok i nie musi jej przebudowywać, gdy część inwentarza przejdzie na warianty.
 *
 * Kwoty NIE są liczone w przeglądarce. Każda zmiana wyboru pyta backend o cenę
 * i o identyfikator wariantu; do czasu odpowiedzi kwota jest nieznana i zakup jest
 * zablokowany. Powtórzenie rachunku „cena edycji plus dopłaty" po stronie klienta
 * dałoby drugie źródło prawdy o cenie, które rozjechałoby się z kwotą zamówienia.
 *
 * Przycisk woła koszyk sam, zamiast przechodzić przez blok przycisku i globalny handler
 * strony. Globalna droga zna wyłącznie identyfikator pracy z kontekstu strony, więc nie
 * miałaby jak przenieść wybranego wariantu; strony zbudowane przed edycjami zostają przy
 * niej bez zmian.
 */
@Component({
  selector: 'app-price-configurator',
  templateUrl: './price-configurator.component.html',
  styleUrls: ['./price-configurator.component.scss'],
  standalone: false,
})
export class PriceConfiguratorComponent implements OnInit, OnDestroy {
  @Input() specialBlock: Block;

  /** Aktualnie wyceniony wybór — punkt zaczepienia dla przekazania wariantu do koszyka. */
  @Output() selectionChange = new EventEmitter<PricedSelection>();

  /** Zgłoszenie zakupu wybranego wariantu; nie leci, gdy wybór jest wyprzedany. */
  @Output() buyRequested = new EventEmitter<PricedSelection>();

  configuration: PriceConfiguration | null = null;
  wybranaEdycja: number | null = null;

  /** Trwa dokładanie pozycji do koszyka — przycisk nie przyjmuje kolejnych kliknięć. */
  dodawanie = false;

  /**
   * Blok, w którym artysta umieści konfigurator, może mieć własną akcję (np. „dodaj do
   * koszyka") — `ClickableBlockDirective` nasłuchuje wtedy kliknięć na całym bloku, w fazie
   * bąbelkowania. Bez zatrzymania propagacji samo rozwinięcie listy edycji odpalałoby akcję
   * rodzica i wyrzucało kupującego do koszyka bez kliknięcia w przycisk zakupu.
   *
   * Konfigurator sam woła koszyk z wybranym wariantem, więc akcja bloku nadrzędnego jest tu
   * zawsze niepożądana — zatrzymujemy ją dla całego wnętrza komponentu, nie tylko dla list.
   */
  zatrzymajAkcjeBlokuNadrzednego(zdarzenie: Event): void {
    zdarzenie.stopPropagation();
  }

  /** Nie udało się wczytać konfiguracji pracy — karta nie ma z czego zbudować wyborów. */
  bladKonfiguracji = false;

  /** Nie udało się wycenić bieżącego wyboru — kwota jest nieznana, zakup zablokowany. */
  bladWyceny = false;

  private wycena: PricedSelection | null = null;
  private sprzedazDostepna = false;
  private wybraneOpcje = new Map<number, number>();
  private jezyk = DOMYSLNY_JEZYK;
  private readonly wybory = new Subject<{ edycja: number | null; opcje: number[] }>();
  private readonly subskrypcje: Subscription[] = [];

  /** Odpięcie globalnego nasłuchu kliknięć, który zamyka panele po kliknięciu poza konfiguratorem. */
  private odepnijNasluchKlikniec: (() => void) | null = null;

  constructor(
    private pageService: PageService,
    private apiEditionConfigurationService: ApiEditionConfigurationService,
    private sessionStorageService: SessionStorageService,
    private shoppingCartService: ShoppingCartService,
    private apiInventoryService: ApiInventoryService,
    private translateService: TranslateService,
    private renderer: Renderer2,
    private router: Router,
  ) {
  }

  /**
   * Formularz list rozwijanych. Kontrolki zakładamy dopiero, gdy wiadomo, ile parametrów
   * ma praca — parametr główny plus po jednej na grupę opcji. Wybór idzie przez formularz,
   * a nie przez zdarzenie `selectionChange` pola, bo pola pochodzą teraz ze struktury bloku
   * i mogą zostać przez artystę przestawione albo ostylowane; formularz jest jedynym
   * punktem, który to przeżywa.
   */
  readonly formGroup = new FormGroup({});

  /** Listy rozwijane do wyrenderowania — powstają z jednego wzorca ze struktury. */
  parametry: ParametrWidoku[] = [];

  /** Węzły ze strukturą, z wstrzyniętą treścią. `null`, gdy blok nie ma jeszcze struktury. */
  wezelCeny: Block | null = null;
  wezelPrzycisku: Block | null = null;
  wezelTekstuBledu: Block | null = null;
  wezelPonowienia: Block | null = null;

  /**
   * Czy blok ma strukturę, z której da się złożyć widok.
   *
   * Bloki założone przed AT-9279 mają `structure = []` — dla nich nie ma czego renderować
   * i nie udajemy, że jest. Artysta musi taki blok dodać na nowo, żeby fabryka szablonu
   * zbudowała mu drzewo; naprawianie go w locie po stronie sklepu ukryłoby problem
   * i zostawiło stronę bez możliwości stylowania.
   */
  get maStrukture(): boolean {
    return !!this.kontener;
  }

  private get kontener(): Block | null {
    return this.dziecko(this.specialBlock?.children, ViewPart.PRICE_CONFIGURATOR_CONTAINER);
  }

  get wezelKontenera(): Block | null {
    return this.kontener;
  }

  get wezelBledu(): Block | null {
    return this.dziecko(this.kontener?.children, ViewPart.PRICE_CONFIGURATOR_ERROR);
  }

  get edycje(): EditionChoice[] {
    return this.configuration?.editions ?? [];
  }

  get grupyOpcji(): EditionOptionGroup[] {
    return this.configuration?.optionGroups ?? [];
  }

  /** Listy rozwijane pokazujemy tylko pracy, która naprawdę ma z czego wybierać. */
  get pokazujeEdycje(): boolean {
    return !!this.configuration?.hasEditions && this.edycje.length > 0;
  }

  /**
   * Blok zostaje na karcie także po nieudanym odczycie konfiguracji.
   *
   * Zniknięcie bloku zabiera z karty kwotę i jedyną drogę do koszyka, a kupujący nie ma
   * jak odróżnić awarii od pracy, której artysta po prostu nie wystawił na sprzedaż.
   * Zostawiony blok z komunikatem i ponowieniem zamienia trwałą, cichą utratę sprzedaży
   * w widoczny i odwracalny epizod.
   */
  get pokazujeBlok(): boolean {
    return !!this.configuration || this.bladKonfiguracji;
  }

  /** Klucz tłumaczenia komunikatu; brak konfiguracji jest cięższy niż brak samej kwoty. */
  get komunikatBledu(): string {
    return this.bladKonfiguracji ? KOMUNIKAT_BLEDU_KONFIGURACJI : KOMUNIKAT_BLEDU_WYCENY;
  }

  /** Czy pokazać kupującemu komunikat o nieudanym odczycie razem z przyciskiem ponowienia. */
  get pokazujeBlad(): boolean {
    return this.bladKonfiguracji || this.bladWyceny;
  }

  get etykietaGlownegoParametru(): string {
    return this.tekst(this.configuration?.mainParameterLabel);
  }

  get cena(): number | null {
    return this.wycena ? this.wycena.price : null;
  }

  get waluta(): string {
    return this.configuration?.currency ?? '';
  }

  get wariant(): number | null {
    return this.wycena ? this.wycena.variantId : null;
  }

  /**
   * Zakup wymaga jednocześnie sztuk na wybranej edycji i pracy wystawionej na sprzedaż.
   *
   * Konfiguracja cenowa nie zna statusu inwentarza, więc sama dostępność nakładu
   * przepuściłaby pracę wycofaną ze sprzedaży, która ma jeszcze zapas. Kanoniczna brama
   * sprzedaży całej pracy stoi osobno i pyta o nią sklep — patrz `sprzedazDostepna`.
   */
  /**
   * Czy przycisk zakupu ma w ogóle ISTNIEĆ na stronie.
   *
   * To nie jest to samo co `mozeKupic`. Status pracy rozstrzyga o istnieniu wezwania
   * do zakupu: praca wycofana ze sprzedaży nie pokazuje go wcale — tak samo jak zwykła
   * praca, której dyrektywa `canShow` po prostu nie renderuje. Brak sztuk i trwające
   * dokładanie do koszyka jedynie BLOKUJĄ przycisk, bo dotyczą chwili, a nie tego,
   * czy praca jest w sprzedaży.
   *
   * Dlatego ukrywanie nie może wisieć na `mozeKupic`: zdejmowałoby przycisk także
   * wyprzedanej edycji i kupującemu spod palca na czas dokładania do koszyka.
   *
   * Brak pola w odpowiedzi (rozjazd wdrożeń backendu i sklepu) traktujemy jak zgodę —
   * schowanie wezwania do zakupu przez nieobecne pole byłoby gorsze niż pokazanie go
   * o jedno wdrożenie za długo.
   */
  get pokazacPrzyciskZakupu(): boolean {
    return this.configuration?.statusAllowsBuyButton !== false;
  }

  get mozeKupic(): boolean {
    return this.sprzedazDostepna
      && !this.dodawanie
      && !!this.wycena
      && this.wycena.availableQuantity > 0;
  }

  /**
   * Napis na przycisku bierzemy z bloku, nie z konfiguracji edycji — blok przychodzi
   * z backendu rozstrzygnięty dla jednego języka strony, więc nie wymaga mapy tłumaczeń.
   *
   * Pierwsza jest treść bloku, bo to ona ma osobną wartość w każdym języku i to ją zapisuje
   * panel. Ustawienie bloku zostaje tylko jako zaplecze dla bloków założonych wcześniej —
   * trzymało jeden napis na wszystkie języki, więc nowy, przetłumaczony musi je przykryć.
   */
  get etykietaPrzycisku(): string {
    return this.specialBlock?.content || this.specialBlock?.settings?.buttonLabel || '';
  }

  ngOnInit(): void {
    this.jezyk = this.sessionStorageService.getItem('language') || DOMYSLNY_JEZYK;

    // Wycena idzie przez switchMap, bo kupujący klika po listach szybciej, niż wracają
    // odpowiedzi. Bez tego spóźniona odpowiedź poprzedniego wyboru nadpisałaby kwotę
    // aktualnego i kupujący widziałby cenę wariantu, którego już nie ma wybranego.
    this.subskrypcje.push(
      this.wybory.pipe(
        switchMap(({edycja, opcje}) => this.apiEditionConfigurationService.priceFor(this.idPracy(), edycja, opcje).pipe(
          // Błąd gasimy WEWNĄTRZ strumienia pojedynczej wyceny, żeby nie doszedł do
          // subskrybenta. Ten strumień żyje tak długo jak karta pracy, więc błąd puszczony
          // na zewnątrz zamknąłby go na stałe: kolejne kliknięcia w listy trafiałyby w
          // nikogo, kwota nigdy by się nie pokazała, a przycisk zakupu zostałby martwy do
          // przeładowania strony. Jedno mignięcie sieci kosztowałoby całą sprzedaż z tej karty.
          catchError(() => of(null)),
        )),
      ).subscribe(wycena => this.przyjmijWycene(wycena)),
    );

    this.subskrypcje.push(
      this.pageService.page.subscribe(strona => {
        if (strona?.modelId) {
          this.wczytajKonfiguracje(strona.modelId);
        }
      }),
    );

    // Panel opcji zamyka się po kliknięciu poza konfiguratorem — jak lista sortowania.
    // Kliknięcia wewnątrz nie docierają tu, bo `zatrzymajAkcjeBlokuNadrzednego` zatrzymuje
    // propagację na powłoce; sprawdzenie `closest` zostaje jako druga bariera na wypadek,
    // gdyby zdarzenie mimo to weszło (np. z elementu przeniesionego poza powłokę).
    this.odepnijNasluchKlikniec = this.renderer.listen('document', 'click', (zdarzenie: Event) => {
      const cel = zdarzenie.target as HTMLElement;
      if (!cel?.closest('app-price-configurator')) {
        this.zamknijPanele();
      }
    });
  }

  ngOnDestroy(): void {
    this.subskrypcje.forEach(subskrypcja => subskrypcja.unsubscribe());
    if (this.odepnijNasluchKlikniec) {
      this.odepnijNasluchKlikniec();
      this.odepnijNasluchKlikniec = null;
    }
    // Wymiary są związane z tą kartą pracy — po zejściu ze strony snippet nie może
    // pokazywać wartości z poprzedniego wyboru na kolejnej pracy.
    EditionDimensionsService.reset();
  }

  /** Tekst z mapy tłumaczeń dla języka strony, z cofnięciem na pierwszy dostępny. */
  tekst(mapa: LocaleMap | null | undefined): string {
    if (!mapa) {
      return '';
    }

    return mapa[this.jezyk] ?? mapa[DOMYSLNY_JEZYK] ?? Object.values(mapa)[0] ?? '';
  }

  /**
   * Klasy węzła dla elementów, których NIE renderuje `app-block` (panel listy, wiersz opcji).
   * Reguła stylu artysty ma postać `.block_<uId>.m<modelId>`, więc sama klasa `uId` jej nie
   * uruchomi — `app-block` dokłada `m<modelId>` samodzielnie, tutaj musimy zrobić to ręcznie.
   */
  klasyWezla(wezel: Block | null | undefined): string {
    if (!wezel?.uId) {
      return '';
    }

    return wezel.modelId ? `${wezel.uId} m${wezel.modelId}` : wezel.uId;
  }

  /**
   * Wyprzedanej edycji nie ukrywamy — informacja o wyczerpanym nakładzie ma wartość
   * rynkową, bo mówi kupującemu, że seria się rozeszła.
   */
  edycjaWyprzedana(edycja: EditionChoice): boolean {
    return edycja.availableQuantity <= 0;
  }

  wybranaOpcja(grupaId: number): number | null {
    return this.wybraneOpcje.get(grupaId) ?? null;
  }

  wybierzEdycje(edycjaId: number): void {
    this.wybranaEdycja = edycjaId;
    this.przeliczCene();
  }

  wybierzOpcje(grupaId: number, opcjaId: number): void {
    this.wybraneOpcje.set(grupaId, opcjaId);
    this.przeliczCene();
  }

  /**
   * Otwiera albo zamyka panel opcji parametru. Naraz otwarty jest najwyżej jeden panel,
   * więc przed otwarciem zamykamy pozostałe — jak lista sortowania.
   */
  przelaczPanel(parametr: ParametrWidoku): void {
    const otwarty = !parametr.otwarty;
    this.zamknijPanele();
    parametr.otwarty = otwarty;
  }

  /** Zamyka wszystkie panele — wywoływane po wyborze i po kliknięciu poza konfiguratorem. */
  zamknijPanele(): void {
    this.parametry.forEach(parametr => (parametr.otwarty = false));
  }

  /**
   * Wybór opcji z panelu. Wyprzedanej nie da się wybrać. Wybór idzie przez `setValue`
   * na kontrolce formularza, więc odpala tę samą ścieżkę co wcześniej mat-select:
   * `valueChanges` → `przyjmijWybor` → `przeliczCene`. Ponowny wybór tej samej wartości
   * nie rusza kontrolki (mat-select też nie emitował) — zamykamy tylko panel.
   */
  wybierz(parametr: ParametrWidoku, opcja: OpcjaWidoku): void {
    if (opcja.wyprzedana) {
      return;
    }

    const kontrolka = this.formGroup.get(parametr.klucz);
    if (kontrolka && kontrolka.value !== opcja.id) {
      kontrolka.setValue(opcja.id);
    }

    parametr.otwarty = false;
  }

  /** Tekst prostego triggera zastępczego: etykieta wybranej opcji (blok bez węzła triggera). */
  tekstTriggera(parametr: ParametrWidoku): string {
    const wartosc = this.formGroup.get(parametr.klucz)?.value ?? null;

    return this.etykietaOpcji(parametr.opcje, wartosc);
  }

  /** Enter/Spacja na triggerze — otwarcie/zamknięcie panelu; blokujemy domyślne przewinięcie. */
  przelaczPanelKlawiszem(parametr: ParametrWidoku, zdarzenie: Event): void {
    zdarzenie.preventDefault();
    this.przelaczPanel(parametr);
  }

  /**
   * Strzałka w dół/górę na triggerze otwiera panel i przenosi fokus na pierwszą/ostatnią opcję.
   * Fokus ustawiamy po cyklu renderowania, bo panel przed otwarciem ma `display:none`.
   */
  otworzZFokusem(parametr: ParametrWidoku, zdarzenie: Event, doPierwszej: boolean): void {
    zdarzenie.preventDefault();
    if (!parametr.otwarty) {
      this.przelaczPanel(parametr);
    }

    const powloka = (zdarzenie.target as HTMLElement).closest('.price-configurator__select');
    setTimeout(() => {
      const opcje = this.opcjeWPanelu(powloka);
      const cel = doPierwszej ? opcje[0] : opcje[opcje.length - 1];
      cel?.focus();
    });
  }

  /** Wybór opcji z klawiatury — jak kliknięcie, dodatkowo cofa fokus na trigger. */
  wybierzKlawiszem(parametr: ParametrWidoku, opcja: OpcjaWidoku, zdarzenie: Event): void {
    zdarzenie.preventDefault();
    this.wybierz(parametr, opcja);
    this.fokusujTrigger(zdarzenie);
  }

  /** Strzałki w panelu przenoszą fokus między opcjami. */
  nawigujOpcje(zdarzenie: Event, kierunek: number): void {
    zdarzenie.preventDefault();
    const biezacy = zdarzenie.target as HTMLElement;
    const opcje = this.opcjeWPanelu(biezacy.closest('.price-configurator__options'));
    const indeks = opcje.indexOf(biezacy);
    opcje[indeks + kierunek]?.focus();
  }

  /** Escape z panelu zamyka listę i cofa fokus na trigger. */
  zamknijZPanelu(zdarzenie: Event): void {
    this.zamknijPanele();
    this.fokusujTrigger(zdarzenie);
  }

  private opcjeWPanelu(powloka: Element | null | undefined): HTMLElement[] {
    return Array.from(
      powloka?.querySelectorAll<HTMLElement>('.price-configurator__option') ?? [],
    );
  }

  private fokusujTrigger(zdarzenie: Event): void {
    const trigger = (zdarzenie.target as HTMLElement)
      .closest('.price-configurator__select')
      ?.querySelector<HTMLElement>('.price-configurator__trigger');
    trigger?.focus();
  }

  /**
   * Dokłada wybrany wariant do koszyka i przenosi tam kupującego.
   *
   * Wariant jest częścią tożsamości pozycji, więc dwie edycje tej samej pracy zajmą
   * dwie pozycje koszyka, a nie jedną o podwojonej ilości. Kwoty do koszyka nie
   * przekazujemy — liczy je backend z identyfikatora wariantu.
   *
   * Przejście robimy tutaj, bo konfigurator woła koszyk z pominięciem globalnego handlera
   * strony (musi przenieść wybrany wariant, którego tamta droga nie zna) i nie odziedziczy
   * po nim nawigacji.
   */
  async kup(): Promise<void> {
    if (!this.mozeKupic) {
      return;
    }

    const wybor = this.wycena;
    this.buyRequested.emit(wybor);
    this.dodawanie = true;
    let dolozone = false;

    try {
      await this.dodajDoKoszyka(wybor);
      dolozone = true;
    } catch {
      // Serwis koszyka zgłosił błąd i schował swój loader. Przycisk wraca do stanu
      // gotowego, żeby kupujący mógł spróbować ponownie.
    } finally {
      this.dodawanie = false;
    }

    // Tylko po udanym dołożeniu: przy zerwanym połączeniu w koszyku nie ma nowej pozycji,
    // więc przeniesienie pokazałoby kupującemu pusty koszyk zamiast błędu. Zostaje na
    // karcie, gdzie przycisk jest już z powrotem gotowy na kolejne kliknięcie.
    if (dolozone) {
      await this.router.navigate([`/${this.jezyk}/cart`]);
    }
  }

  /**
   * Ponawia nieudany odczyt po kliknięciu kupującego.
   *
   * Błąd konfiguracji zabiera całą kartę, więc wracamy po nią od początku — razem z bramą
   * sprzedaży, która mogła paść przy tym samym mignięciu sieci. Błąd samej wyceny wymaga
   * wyłącznie powtórzenia pytania o bieżący wybór.
   */
  ponow(): void {
    const idPracy = this.idPracy();

    if (this.bladKonfiguracji) {
      if (idPracy) {
        this.wczytajKonfiguracje(idPracy);
      }

      return;
    }

    this.przeliczCene();
  }

  private dodajDoKoszyka(wybor: PricedSelection): Promise<void> {
    const strona = this.pageService.page.value;
    // Kontekst czytamy przy kliknięciu, a nie przy wejściu na stronę: kupujący mógł
    // dotąd przejść między kartą katalogową a podglądem prywatnej oferty.
    const {parentType, grupID} = cartSourceContextFromUrl(this.adresStrony());

    return this.shoppingCartService.add(
      strona?.modelId,
      strona?.basketItemType,
      parentType,
      grupID,
      wybor.variantId,
    );
  }

  /** Adres oglądanej strony; czytany dopiero przy kliknięciu, więc zawsze w przeglądarce. */
  private adresStrony(): string {
    return typeof window === 'undefined' ? '' : window.location.href;
  }

  private wczytajKonfiguracje(idPracy: number): void {
    this.bladKonfiguracji = false;

    this.subskrypcje.push(
      this.apiEditionConfigurationService.configurationFor(idPracy).subscribe({
        next: konfiguracja => {
          this.configuration = konfiguracja;
          this.ustawWybor(konfiguracja);
          this.zbudujWidok();
        },
        // Bez konfiguracji nie ma z czego zbudować wyborów, więc kartę zostawiamy pustą,
        // ale widoczną — stan błędu z ponowieniem zamiast bloku znikającego bez słowa.
        error: () => {
          this.configuration = null;
          this.wycena = null;
          this.bladKonfiguracji = true;
          this.zbudujWidok();
        },
      }),
    );

    this.sprawdzSprzedaz(idPracy);
  }

  /**
   * Pyta kanoniczną bramę sprzedaży o całą pracę: status inwentarza plus zapas.
   *
   * Blok konfiguratora nie przechodzi przez dyrektywę zasłaniającą bloki przycisku
   * (`canShow`), a poza tą bramą statusu nie sprawdza w sklepie nikt — ani walidacja
   * koszyka, ani składanie zamówienia. Bez tego pytania praca wycofana ze sprzedaży,
   * ale z niesprzedanym nakładem, dałaby się kupić do końca.
   *
   * Nieudana odpowiedź zostawia zakup zablokowany: przy bramie sprzedaży fałszywa
   * zgoda kosztuje więcej niż fałszywa odmowa.
   */
  private sprawdzSprzedaz(idPracy: number): void {
    this.sprzedazDostepna = false;

    this.apiInventoryService.buyIsAvailable(idPracy)
      .then(dostepna => this.sprzedazDostepna = dostepna === true)
      .catch(() => this.sprzedazDostepna = false);
  }

  /**
   * Wybór początkowy rozstrzyga backend — odsyła go w `selection` razem z wyceną.
   * Flagi `isDefault` są tu tylko zabezpieczeniem na odpowiedź, która wyboru nie
   * rozstrzygnęła: wolimy pokazać domyślną edycję bez kwoty niż pustą kontrolkę.
   */
  private ustawWybor(konfiguracja: PriceConfiguration): void {
    this.wybranaEdycja = this.edycjaPoczatkowa(konfiguracja);
    this.wybraneOpcje = this.dopasujOpcje(konfiguracja, konfiguracja.selection?.optionIds ?? []);

    if (this.wyborZgodnyZWycena(konfiguracja.selection)) {
      this.zapamietajWycene(konfiguracja.selection);

      return;
    }

    this.przeliczCene();
  }

  /**
   * Edycja pokazana kupującemu, gdy wchodzi na kartę.
   *
   * Rozstrzyga backend i odsyła wynik w `selection.editionId` — front go honoruje bez
   * własnego sprawdzania. Reguła preferencji (domyślność artysty, dopóki edycja ma sztuki,
   * potem pierwsza dostępna) mieszka wyłącznie w `PriceConfigurationProvider`, bo ta sama
   * reguła rządzi drogą bez wariantu: zejściem ze stanu, walidacją ilości i kwotami dostawy.
   * Powtórzona tutaj jako pełnoprawna decyzja dałaby drugie źródło prawdy, które po zmianie
   * reguły na serwerze przykryłoby ją po cichu i nikt by tego nie zauważył.
   */
  private edycjaPoczatkowa(konfiguracja: PriceConfiguration): number | null {
    const wskazana = konfiguracja.selection?.editionId ?? null;

    return wskazana !== null ? wskazana : this.edycjaZapasowa(konfiguracja);
  }

  /**
   * Zabezpieczenie na odpowiedź, która wyboru nie rozstrzygnęła: powtarza preferencję
   * backendu, żeby kupujący dostał kontrolkę z sensowną wartością zamiast pustej.
   * Wyprzedany cały nakład zostaje przy edycji domyślnej — przycisk jest wtedy
   * zablokowany zgodnie z prawdą i nie ma na co przeskakiwać.
   */
  private edycjaZapasowa(konfiguracja: PriceConfiguration): number | null {
    const domyslna = this.domyslnaEdycja(konfiguracja);

    if (domyslna === null || this.edycjaMaSztuki(konfiguracja, domyslna)) {
      return domyslna;
    }

    const dostepna = konfiguracja.editions?.find(edycja => !this.edycjaWyprzedana(edycja));

    return dostepna ? dostepna.id : domyslna;
  }

  private edycjaMaSztuki(konfiguracja: PriceConfiguration, edycjaId: number): boolean {
    const edycja = konfiguracja.editions?.find(pozycja => pozycja.id === edycjaId);

    return !!edycja && !this.edycjaWyprzedana(edycja);
  }

  private domyslnaEdycja(konfiguracja: PriceConfiguration): number | null {
    const domyslna = konfiguracja.editions?.find(edycja => edycja.isDefault) ?? konfiguracja.editions?.[0];

    return domyslna ? domyslna.id : null;
  }

  /**
   * Dla każdej grupy parametrów wybiera opcję wskazaną przez backend, a gdy jej nie ma —
   * opcję domyślną grupy. Pusty dropdown bez preselekcji nie da się wycenić, więc na
   * końcu bierzemy pierwszą opcję grupy.
   */
  private dopasujOpcje(konfiguracja: PriceConfiguration, wskazane: number[]): Map<number, number> {
    const wybrane = new Map<number, number>();

    (konfiguracja.optionGroups ?? []).forEach(grupa => {
      const opcja = grupa.options?.find(pozycja => wskazane.includes(pozycja.id))
        ?? grupa.options?.find(pozycja => pozycja.isDefault)
        ?? grupa.options?.[0];

      if (opcja) {
        wybrane.set(grupa.id, opcja.id);
      }
    });

    return wybrane;
  }

  private wyborZgodnyZWycena(wycena: PricedSelection | null | undefined): boolean {
    if (!wycena) {
      return false;
    }

    const wybraneOpcje = this.idWybranychOpcji();

    return wycena.editionId === this.wybranaEdycja
      && wycena.optionIds?.length === wybraneOpcje.length
      && wybraneOpcje.every(opcjaId => wycena.optionIds.includes(opcjaId));
  }

  /**
   * Kasujemy poprzednią kwotę od razu przy zmianie wyboru. Pokazywanie ceny poprzedniej
   * edycji obok nazwy nowej to najgorszy możliwy stan przejściowy w sklepie.
   */
  private przeliczCene(): void {
    this.wycena = null;
    this.bladWyceny = false;
    this.odswiezCene();
    this.wybory.next({edycja: this.wybranaEdycja, opcje: this.idWybranychOpcji()});
  }

  /**
   * Wynik pytania o cenę; `null` znaczy, że odpowiedź nie doszła.
   *
   * Nieudana wycena zawodzi zamknięta: kwota zostaje nieznana, a zakup zablokowany.
   * Podstawienie poprzedniej kwoty pokazywałoby cenę wariantu, którego kupujący już
   * nie ma wybranego, a przepuszczenie zakupu bez potwierdzonej kwoty jest w sklepie
   * droższe niż odmowa.
   */
  private przyjmijWycene(wycena: PricedSelection | null): void {
    if (wycena === null) {
      this.wycena = null;
      this.bladWyceny = true;
      // Zdejmujemy wariant, żeby po nieudanej wycenie przycisk strony nie dołożył do
      // koszyka poprzedniego wyboru — kupujący widzi wtedy błąd, nie kwotę.
      this.shoppingCartService.zapamietajWariant(this.idPracy(), null);
      this.odswiezCene();
      this.odswiezPrzyciskIBlad();

      return;
    }

    this.bladWyceny = false;
    this.zapamietajWycene(wycena);
    this.odswiezCene();
    this.odswiezPrzyciskIBlad();
  }

  private zapamietajWycene(wycena: PricedSelection): void {
    this.wycena = wycena;
    // Wybór musi dojść do serwisu koszyka, bo przycisk „Kup" karty pracy jest zwykłym
    // blokiem strony i obsługuje go main-page, który tego komponentu nie zna. Bez tego
    // do koszyka szedł wariant `null`, a backend cofał się do edycji domyślnej.
    this.shoppingCartService.zapamietajWariant(this.idPracy(), wycena.variantId);
    // Wymiary wybranej edycji (z fallbackiem na pracę) zasilają snippet „Wymiary". Podajemy
    // je wyłącznie z potwierdzonej wyceny — przy zmianie edycji snippet zmienia się dopiero,
    // gdy backend odeśle wymiary nowego wyboru, więc nigdy nie miga wartością z innej edycji.
    EditionDimensionsService.update(formatSelectionDimensions(wycena.dimensions));
    this.selectionChange.emit(wycena);
  }

  private idWybranychOpcji(): number[] {
    return this.grupyOpcji
      .map(grupa => this.wybraneOpcje.get(grupa.id))
      .filter((opcjaId): opcjaId is number => opcjaId !== undefined);
  }

  /** Węzeł o wskazanej roli wśród podanych dzieci. */
  private dziecko(dzieci: Block[] | undefined, rola: ViewPart): Block | null {
    return (dzieci ?? []).find(dziecko => dziecko?.settings?.viewPart === rola) ?? null;
  }

  /**
   * Węzeł o wskazanej roli gdziekolwiek w poddrzewie — najpierw wśród dzieci, potem głębiej.
   *
   * Potrzebne dla listy opcji: od AT-9352 fabryka zagnieżdża ją jak w sklepie (panel › tło
   * opcji › nazwa opcji), a bloki założone wcześniej mają te węzły płasko pod wierszem
   * parametru. Szukanie najpierw na pierwszym poziomie obsługuje oba układy bez rozróżniania.
   */
  private findDescendant(children: Block[] | undefined, role: ViewPart): Block | null {
    const direct = this.dziecko(children, role);
    if (direct) {
      return direct;
    }

    for (const child of children ?? []) {
      const nested = this.findDescendant(child?.children, role);
      if (nested) {
        return nested;
      }
    }

    return null;
  }

  /**
   * Klon węzła z podmienioną treścią.
   *
   * Klonujemy, a nie podmieniamy treść na oryginale, bo z jednego wzorca powstaje wiele
   * elementów (lista edycji, listy opcji) — wspólny obiekt pokazałby wszędzie tę samą nazwę.
   * `clone()` zachowuje `uId`, więc style zapisane przez artystę jadą z każdym klonem.
   *
   * Nowy klon powstaje tylko wtedy, gdy treść naprawdę się zmieniła. Inaczej każdy cykl
   * detekcji zmian dawałby nową referencję i `app-block` przebudowywałby element bez powodu.
   */
  private zTrescia(wzorzec: Block | null, tresc: string, poprzedni: Block | null = null): Block | null {
    if (!wzorzec) {
      return null;
    }

    if (poprzedni && poprzedni.content === tresc) {
      return poprzedni;
    }

    const klon = wzorzec.clone();
    klon.content = tresc;

    return klon;
  }

  /**
   * Składa węzeł przycisku zakupu z napisem z bloku.
   *
   * Od AT-9406 przycisk to kontener (akcja, ramka, tło) z paragrafem o roli `BUY_BUTTON_TEXT`
   * w środku, więc napis podmieniamy w tym dziecku, a nie w kontenerze — `uId` kontenera i
   * paragrafu zostają, bo na nich siada styl artysty. Blok założony przed AT-9406 ma przycisk
   * jako pojedynczy węzeł tekstowy, bez takiego dziecka; dostaje napis wprost, jak dotąd.
   *
   * Klon powstaje tylko, gdy napis naprawdę się zmienił (jak w `zTrescia`), żeby `app-block`
   * nie przebudowywał przycisku w każdym cyklu detekcji zmian.
   */
  private zlozPrzycisk(wzorzec: Block | null, etykieta: string, poprzedni: Block | null): Block | null {
    if (!wzorzec) {
      return null;
    }

    const maParagraf = !!this.dziecko(wzorzec.children, ViewPart.PRICE_CONFIGURATOR_BUY_BUTTON_TEXT);
    if (!maParagraf) {
      return this.zTrescia(wzorzec, etykieta, poprzedni);
    }

    if (poprzedni && this.dziecko(poprzedni.children, ViewPart.PRICE_CONFIGURATOR_BUY_BUTTON_TEXT)?.content === etykieta) {
      return poprzedni;
    }

    const klon = wzorzec.clone();
    const tekst = this.dziecko(klon.children, ViewPart.PRICE_CONFIGURATOR_BUY_BUTTON_TEXT);
    if (tekst) {
      tekst.content = etykieta;
    }

    return klon;
  }

  /**
   * Składa węzeł triggera z tekstem aktualnie wybranej opcji.
   *
   * Trigger to kontener (obramowanie) z dzieckiem-tekstem i ikoną strzałki. Treść podmieniamy
   * WYŁĄCZNIE w dziecku o roli `TRIGGER_TEXT`, zostawiając `uId` kontenera i strzałki — na nich
   * siada styl artysty. Pusta etykieta (brak wyboru) zostawia domyślny tekst węzła („Wybierz").
   *
   * Klon powstaje tylko, gdy tekst naprawdę się zmienił — inaczej każdy cykl detekcji zmian
   * dawałby nową referencję i `app-block` przebudowywałby trigger bez powodu (jak w `zTrescia`).
   *
   * `tabindex` ustawiamy na samym węźle, bo `app-block` zdejmuje atrybut `tabindex` z hosta,
   * gdy blok go nie niesie — bez tego trigger nie łapałby fokusu z klawiatury.
   */
  private zlozTrigger(wzorzec: Block | null, tekst: string, poprzedni: Block | null): Block | null {
    if (!wzorzec) {
      return null;
    }

    const docelowy = tekst || this.domyslnyTekstTriggera(wzorzec);
    const poprzedniTekst = this.dziecko(poprzedni?.children, ViewPart.PRICE_CONFIGURATOR_TRIGGER_TEXT)?.content;
    if (poprzedni && poprzedniTekst === docelowy) {
      return poprzedni;
    }

    const klon = wzorzec.clone();
    klon.tabindex = 0;

    const wezelTekstu = this.dziecko(klon.children, ViewPart.PRICE_CONFIGURATOR_TRIGGER_TEXT);
    if (wezelTekstu) {
      wezelTekstu.content = docelowy;
    }

    return klon;
  }

  /** Domyślny tekst triggera to treść węzła `TRIGGER_TEXT` ze wzorca (np. „Wybierz"). */
  private domyslnyTekstTriggera(wzorzec: Block | null): string {
    return this.dziecko(wzorzec?.children, ViewPart.PRICE_CONFIGURATOR_TRIGGER_TEXT)?.content ?? '';
  }

  /** Etykieta opcji o danym id, czytana z jej węzła; brak dopasowania → pusty tekst. */
  private etykietaOpcji(opcje: OpcjaWidoku[], wybranyId: number | null): string {
    return opcje.find(opcja => opcja.id === wybranyId)?.wezel?.content ?? '';
  }

  /** Składa widok ze struktury bloku — wołane, gdy zmieni się konfiguracja pracy. */
  private zbudujWidok(): void {
    this.zbudujParametry();
    this.odswiezPrzyciskIBlad();
    this.odswiezCene();
  }

  private zbudujParametry(): void {
    const wzorzec = this.dziecko(this.kontener?.children, ViewPart.PRICE_CONFIGURATOR_PARAMETER);

    if (!wzorzec || !this.pokazujeEdycje) {
      this.parametry = [];

      return;
    }

    const etykietaWzor = this.dziecko(wzorzec.children, ViewPart.PRICE_CONFIGURATOR_PARAMETER_LABEL);
    const opcjaWzor = this.findDescendant(wzorzec.children, ViewPart.PRICE_CONFIGURATOR_OPTION);
    // Trigger i wrapper wiersza istnieją tylko w nowszych blokach. Blok starszy zwraca tu
    // `null` — szablon pokazuje wtedy prosty trigger zastępczy, bez stylowania per `uId`.
    const triggerWzor = this.dziecko(wzorzec.children, ViewPart.PRICE_CONFIGURATOR_TRIGGER);
    const optionRowWzor = this.findDescendant(wzorzec.children, ViewPart.PRICE_CONFIGURATOR_OPTION_ROW);
    // Węzeł panelu istnieje tylko w blokach założonych po tej poprawce; blok starszy zwraca
    // tu `null` — szablon wtedy po prostu nie dostaje żadnej klasy zakresu na panelu.
    const panelWzor = this.findDescendant(wzorzec.children, ViewPart.PRICE_CONFIGURATOR_PANEL);

    const parametryEdycji: ParametrWidoku = this.zbudujParametr(
      KONTROLKA_EDYCJI,
      this.etykietaGlownegoParametru,
      this.edycje.map(edycja => ({
        id: edycja.id,
        wezel: this.zTrescia(opcjaWzor, this.tekst(edycja.name)),
        wyprzedana: this.edycjaWyprzedana(edycja),
      })),
      this.wybranaEdycja,
      wzorzec,
      etykietaWzor,
      triggerWzor,
      optionRowWzor,
      panelWzor,
    );

    const parametry: ParametrWidoku[] = [parametryEdycji];

    this.grupyOpcji.forEach(grupa => parametry.push(this.zbudujParametr(
      PRZEDROSTEK_KONTROLKI_GRUPY + grupa.id,
      this.tekst(grupa.label),
      grupa.options.map(opcja => ({
        id: opcja.id,
        wezel: this.zTrescia(opcjaWzor, this.tekst(opcja.name)),
        // Dopłata nie ma własnego stanu magazynowego — wyprzedaje się edycja, nie opcja.
        wyprzedana: false,
      })),
      this.wybranaOpcja(grupa.id),
      wzorzec,
      etykietaWzor,
      triggerWzor,
      optionRowWzor,
      panelWzor,
    )));

    this.parametry = parametry;
    this.zalozKontrolki();
  }

  /**
   * Składa jeden wpis listy: etykietę, opcje oraz trigger z tekstem wartości preselekcjonowanej.
   * Trigger budujemy tu, bo preselekcja w `zalozKontrolki` idzie bez emisji zdarzenia i nie
   * przechodzi przez `przyjmijWybor`, który odświeża trigger przy późniejszych zmianach wyboru.
   */
  private zbudujParametr(
    klucz: string,
    etykietaTekst: string,
    opcje: OpcjaWidoku[],
    wybranyId: number | null,
    wierszWzor: Block | null,
    etykietaWzor: Block | null,
    triggerWzor: Block | null,
    optionRowWzor: Block | null,
    panelWzor: Block | null,
  ): ParametrWidoku {
    return {
      klucz,
      // Wzorca NIE klonujemy z treścią: wszystkie wiersze dzielą jeden węzeł, bo dzielą też
      // jedno stylowanie. Klonowanie dałoby każdemu wierszowi osobny obiekt o tym samym `uId`,
      // czyli ten sam efekt mniejszym kosztem pamięci nie byłby.
      wiersz: wierszWzor,
      etykieta: this.zTrescia(etykietaWzor, etykietaTekst),
      opcje,
      optionRow: optionRowWzor,
      panel: panelWzor,
      otwarty: false,
      trigger: this.zlozTrigger(triggerWzor, this.etykietaOpcji(opcje, wybranyId), null),
    };
  }

  /**
   * Zakłada kontrolki formularza pod listy, po jednej na parametr.
   *
   * Kontrolki zakładamy raz i potem tylko ustawiamy im wartość bez zdarzenia — ponowne
   * `addControl` zgubiłoby subskrypcję i wybór przestałby cokolwiek robić.
   */
  private zalozKontrolki(): void {
    this.parametry.forEach(parametr => {
      const wartosc = parametr.klucz === KONTROLKA_EDYCJI
        ? this.wybranaEdycja
        : this.wybranaOpcja(this.idGrupy(parametr.klucz));

      const istniejaca = this.formGroup.get(parametr.klucz);
      if (istniejaca) {
        istniejaca.setValue(wartosc, {emitEvent: false});

        return;
      }

      const kontrolka = new FormControl(wartosc);
      this.formGroup.addControl(parametr.klucz, kontrolka);
      this.subskrypcje.push(
        kontrolka.valueChanges.subscribe(nowa => this.przyjmijWybor(parametr.klucz, nowa)),
      );
    });
  }

  private przyjmijWybor(klucz: string, wartosc: number | null): void {
    if (wartosc === null || wartosc === undefined) {
      return;
    }

    if (klucz === KONTROLKA_EDYCJI) {
      this.wybierzEdycje(wartosc);
    } else {
      this.wybierzOpcje(this.idGrupy(klucz), wartosc);
    }

    // Tekst triggera odświeżamy tutaj, bo tędy przechodzi każda zmiana wyboru z emisją —
    // zarówno kliknięcie w opcję, jak i przyszłe programowe `setValue` bez `emitEvent:false`.
    this.odswiezTriggerParametru(klucz, wartosc);
  }

  /** Podmienia w triggerze parametru tekst na etykietę aktualnie wybranej opcji. */
  private odswiezTriggerParametru(klucz: string, wartosc: number | null): void {
    const parametr = this.parametry.find(pozycja => pozycja.klucz === klucz);
    if (!parametr) {
      return;
    }

    parametr.trigger = this.zlozTrigger(
      parametr.trigger,
      this.etykietaOpcji(parametr.opcje, wartosc),
      parametr.trigger,
    );
  }

  private idGrupy(klucz: string): number {
    return Number(klucz.slice(PRZEDROSTEK_KONTROLKI_GRUPY.length));
  }

  /** Napis na przycisku zakupu i treść komunikatu błędu; przycisk ponowienia zostaje z bloku. */
  private odswiezPrzyciskIBlad(): void {
    const wzorzecPrzycisku = this.dziecko(this.kontener?.children, ViewPart.PRICE_CONFIGURATOR_BUY_BUTTON);
    const etykieta = this.etykietaPrzycisku;

    // Napis z ustawień bloku NADPISUJE treść węzła, ale tylko gdy jest ustawiony. Puste
    // `buttonLabel` zostawia to, co artysta wpisał w kreatorze — inaczej struktura dostawałaby
    // pusty przycisk za każdym razem, gdy ktoś nie wypełnił pola z poprzedniej wersji bloku.
    this.wezelPrzycisku = etykieta
      ? this.zlozPrzycisk(wzorzecPrzycisku, etykieta, this.wezelPrzycisku)
      : wzorzecPrzycisku;

    const blad = this.wezelBledu;
    this.wezelTekstuBledu = this.zTrescia(
      this.dziecko(blad?.children, ViewPart.PRICE_CONFIGURATOR_ERROR_TEXT),
      this.translateService.instant(this.komunikatBledu),
      this.wezelTekstuBledu,
    );
    this.wezelPonowienia = this.dziecko(blad?.children, ViewPart.PRICE_CONFIGURATOR_RETRY_BUTTON);
  }

  /** Kwota wstrzykiwana do węzła ceny; bez potwierdzonej wyceny węzła nie pokazujemy wcale. */
  private odswiezCene(): void {
    const wzorzec = this.dziecko(this.kontener?.children, ViewPart.PRICE_CONFIGURATOR_PRICE);

    this.wezelCeny = this.cena === null
      ? null
      : this.zTrescia(wzorzec, `${this.cena.toFixed(2)} ${this.waluta}`, this.wezelCeny);
  }

  private idPracy(): number {
    return this.pageService.page.value?.modelId;
  }
}
